import React, { useState, useEffect } from "react";
import axios from "../utils/axiosInstance";

const pct = (a, b) => (b ? Math.round((a / b) * 100) : 0);
const fmt = (v) => (typeof v === "number" ? v.toLocaleString() : (v ?? "—"));

/* ─── colour map ─── */
const COLORS = {
  blue:    { hex: "#0047AB", bg: "rgba(0,71,171,0.10)",   text: "#0047AB" },
  green:   { hex: "#20603D", bg: "rgba(32,96,61,0.10)",   text: "#20603D" },
  red:     { hex: "#c0392b", bg: "rgba(192,57,43,0.10)",  text: "#c0392b" },
  amber:   { hex: "#e07b00", bg: "rgba(224,123,0,0.10)",  text: "#e07b00" },
  violet:  { hex: "#6d28d9", bg: "rgba(109,40,217,0.10)", text: "#6d28d9" },
  teal:    { hex: "#0d7377", bg: "rgba(13,115,119,0.10)", text: "#0d7377" },
  rose:    { hex: "#be185d", bg: "rgba(190,24,93,0.10)",  text: "#be185d" },
  indigo:  { hex: "#3730a3", bg: "rgba(55,48,163,0.10)",  text: "#3730a3" },
  sky:     { hex: "#0369a1", bg: "rgba(3,105,161,0.10)",  text: "#0369a1" },
  fuchsia: { hex: "#86198f", bg: "rgba(134,25,143,0.10)", text: "#86198f" },
  lime:    { hex: "#3f6212", bg: "rgba(63,98,18,0.10)",   text: "#3f6212" },
  orange:  { hex: "#c2410c", bg: "rgba(194,65,12,0.10)",  text: "#c2410c" },
};

/* ─── StatCard ─── */
function StatCard({ icon, label, value, colorKey = "blue", sub, progress }) {
  const c = COLORS[colorKey] || COLORS.blue;
  return (
    <div style={{
      background: "#ffffff",
      border: "1px solid #e2e8f0",
      borderTop: `3px solid ${c.hex}`,
      borderRadius: 10,
      padding: "clamp(10px,2vw,16px) clamp(8px,1.5vw,14px)",
      textAlign: "center",
      position: "relative",
      overflow: "hidden",
      boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
      transition: "transform .2s ease, box-shadow .2s ease",
      cursor: "default",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 6,
      minWidth: 0,
      boxSizing: "border-box",
    }}
      onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = "0 6px 18px rgba(0,0,0,0.10)"; }}
      onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.06)"; }}
    >
      {/* icon */}
      <div style={{
        width: 40, height: 40,
        borderRadius: 9,
        background: c.bg,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: "1rem",
        color: c.hex,
        flexShrink: 0,
      }}>
        <i className={`bi bi-${icon}`} />
      </div>

      {/* value */}
      <div style={{
        fontSize: "clamp(1.1rem,3vw,1.65rem)",
        fontWeight: 800,
        color: "#0d1b2a",
        lineHeight: 1,
        letterSpacing: "-0.02em",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        width: "100%",
        fontVariantNumeric: "tabular-nums",
      }}>
        {fmt(value)}
      </div>

      {/* label */}
      <div style={{
        fontSize: "clamp(0.52rem,1.1vw,0.62rem)",
        fontWeight: 700,
        color: "#64748b",
        textTransform: "uppercase",
        letterSpacing: "0.07em",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        width: "100%",
      }}>
        {label}
      </div>

      {/* sub */}
      {sub && (
        <div style={{ fontSize: "clamp(0.5rem,1vw,0.6rem)", color: "#94a3b8", marginTop: 1 }}>
          {sub}
        </div>
      )}

      {/* progress */}
      {progress !== undefined && (
        <div style={{ width: "100%", height: 3, borderRadius: 2, background: "#f1f5f9", overflow: "hidden", marginTop: 2 }}
          role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100}>
          <div style={{ height: "100%", width: `${progress}%`, background: c.hex, borderRadius: 2, transition: "width .9s ease" }} />
        </div>
      )}
    </div>
  );
}

/* ─── SectionTitle ─── */
function SectionTitle({ icon, title, colorKey = "blue" }) {
  const c = COLORS[colorKey] || COLORS.blue;
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10,
      marginTop: "clamp(18px,3vw,28px)",
      marginBottom: "clamp(8px,1.5vw,12px)",
    }}>
      <div style={{
        width: 30, height: 30, borderRadius: 8,
        background: c.bg, color: c.hex,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: "0.85rem", flexShrink: 0,
      }}>
        <i className={`bi bi-${icon}`} />
      </div>
      <span style={{ fontWeight: 700, fontSize: "clamp(0.85rem,2.2vw,0.98rem)", color: "#0d1b2a" }}>
        {title}
      </span>
      <div style={{ flex: 1, height: 1, background: "#e2e8f0" }} />
    </div>
  );
}

/* ─── Responsive grid wrapper ─── */
function Grid({ cols, children }) {
  /* cols = { base, sm, md, lg } */
  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
      gap: "clamp(8px,1.8vw,12px)",
      marginBottom: 4,
    }}
      className={`is-grid-${cols}`}
    >
      {children}
    </div>
  );
}

/* ─── Shield card ─── */
function ShieldCard() {
  return (
    <div style={{
      background: "#ffffff",
      border: "1px solid #e2e8f0",
      borderTop: "3px solid #20603D",
      borderRadius: 10,
      padding: "clamp(10px,2vw,16px)",
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      gap: 8, textAlign: "center",
      boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
      minHeight: 90,
    }}>
      <i className="bi bi-shield-check-fill" style={{ fontSize: "clamp(1.4rem,3.5vw,1.8rem)", color: "#20603D" }} />
      <span style={{ color: "#64748b", fontSize: "clamp(0.65rem,1.4vw,0.76rem)", lineHeight: 1.4 }}>
        Amakuru yose arindwa kandi akoreshwa ku buryo bw'amategeko
      </span>
    </div>
  );
}

/* ─── Skeleton ─── */
function Skeleton() {
  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))",
      gap: 10, marginTop: 16,
    }}>
      {[...Array(8)].map((_, i) => (
        <div key={i} style={{
          height: 100, borderRadius: 10,
          background: "linear-gradient(90deg,#e2e8f0 25%,#f1f5f9 50%,#e2e8f0 75%)",
          backgroundSize: "200% 100%",
          animation: `isShimmer 1.4s ease infinite`,
          animationDelay: `${i * 80}ms`,
        }} />
      ))}
    </div>
  );
}

/* ══════════════════════════════════════════
   MAIN
══════════════════════════════════════════ */
export default function IsiboStatistics() {
  const [stats,   setStats]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const res = await axios.get("/isibo-stats", {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setStats(res.data);
    } catch {
      setError("Habaye ikosa mu gusoma imibare.");
    } finally {
      setLoading(false);
    }
  };

  const tot  = stats?.abaturage_total    || 0;
  const totA = stats?.abana_total        || 0;
  const totU = stats?.urubyiruko_total   || 0;
  const totM = stats?.amakimbirane_total || 0;

  return (
    <>
      {/* ── keyframes only — no class overrides ── */}
      <style>{`
        @keyframes isShimmer {
          0%   { background-position: -200% 0; }
          100% { background-position:  200% 0; }
        }
        @keyframes isSpin {
          to { transform: rotate(360deg); }
        }
        /* Responsive grid columns via media queries */
        @media (max-width: 1023px) { .is-grid-4 { grid-template-columns: repeat(2, minmax(0,1fr)) !important; } }
        @media (max-width:  767px) { .is-grid-3 { grid-template-columns: repeat(2, minmax(0,1fr)) !important; } }
        @media (max-width:  479px) {
          .is-grid-4,
          .is-grid-3,
          .is-grid-2 { grid-template-columns: repeat(2, minmax(0,1fr)) !important; gap: 7px !important; }
        }
        @media (max-width:  359px) {
          .is-grid-4,
          .is-grid-3,
          .is-grid-2 { gap: 5px !important; }
        }
        @media (hover: none) and (pointer: coarse) {
          .is-stat-card:hover { transform: none !important; box-shadow: 0 1px 4px rgba(0,0,0,0.06) !important; }
        }
        @media (prefers-reduced-motion: reduce) {
          * { animation: none !important; transition: none !important; }
        }
      `}</style>

      {/* ── outer wrapper — inherits dx-content background ── */}
      <div style={{
        maxWidth: 1100,
        margin: "0 auto",
        fontFamily: "'Nunito Sans', -apple-system, BlinkMacSystemFont, sans-serif",
        WebkitFontSmoothing: "antialiased",
        color: "#0d1b2a",
        paddingBottom: "clamp(24px,5vw,48px)",
      }}>

        {/* ── Header ── */}
        <div style={{
          display: "flex", alignItems: "flex-start",
          justifyContent: "space-between", gap: 12,
          marginBottom: "clamp(14px,2.5vw,24px)",
          flexWrap: "wrap",
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* chip */}
            <div style={{
              display: "inline-flex", alignItems: "center", gap: 5,
              padding: "3px 10px", borderRadius: 999,
              background: "rgba(0,71,171,0.08)",
              border: "1px solid rgba(0,71,171,0.2)",
              color: "#0047AB",
              fontSize: "clamp(0.58rem,1.3vw,0.66rem)",
              fontWeight: 700, letterSpacing: "0.08em",
              textTransform: "uppercase", marginBottom: 6,
            }}>
              <i className="bi bi-bar-chart-fill" />
              Imibare y'Umudugudu
            </div>

            <h2 style={{
              fontSize: "clamp(1rem,3vw,1.45rem)",
              fontWeight: 800, color: "#0d1b2a",
              margin: 0, lineHeight: 1.2, letterSpacing: "-0.02em",
            }}>
              📊 Imibare Rusange
            </h2>
            <p style={{ color: "#64748b", fontSize: "clamp(0.7rem,1.6vw,0.8rem)", marginTop: 3 }}>
              Incamake y'amakuru yose yashyizwemo mu mudugudu
            </p>
          </div>

          {/* refresh */}
          <button
            onClick={load}
            title="Vugurura"
            aria-label="Vugurura imibare"
            style={{
              width: 38, height: 38, borderRadius: 8,
              border: "1px solid #e2e8f0",
              background: "#f8fafc", color: "#64748b",
              cursor: "pointer", display: "flex",
              alignItems: "center", justifyContent: "center",
              fontSize: "0.9rem", flexShrink: 0,
              transition: "all .2s",
              WebkitTapHighlightColor: "transparent",
              touchAction: "manipulation",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = "#e2e8f0"; e.currentTarget.style.color = "#0047AB"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "#f8fafc"; e.currentTarget.style.color = "#64748b"; }}
          >
            <i className="bi bi-arrow-clockwise" />
          </button>
        </div>

        {/* ── Error ── */}
        {error && (
          <div style={{
            padding: "10px 14px", borderRadius: 8,
            background: "rgba(192,57,43,0.08)",
            border: "1px solid rgba(192,57,43,0.25)",
            color: "#c0392b", fontSize: "0.84rem",
            fontWeight: 600, display: "flex",
            alignItems: "center", gap: 8, marginBottom: 16,
          }}>
            <i className="bi bi-exclamation-circle-fill" />
            {error}
          </div>
        )}

        {/* ── Loading ── */}
        {loading && (
          <div style={{
            display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
            minHeight: 220, gap: 14,
          }}>
            <div style={{
              width: 36, height: 36, borderRadius: "50%",
              border: "3px solid rgba(0,71,171,0.12)",
              borderTopColor: "#0047AB",
              animation: "isSpin .75s linear infinite",
            }} />
            <p style={{ color: "#64748b", fontSize: "0.84rem" }}>Gutegura imibare...</p>
            <Skeleton />
          </div>
        )}

        {/* ── No data ── */}
        {!loading && !stats && (
          <div style={{
            display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
            minHeight: 220, gap: 14,
          }}>
            <i className="bi bi-database-x" style={{ fontSize: "2rem", color: "#cbd5e1" }} />
            <p style={{ color: "#c0392b", fontSize: "0.84rem" }}>Nta mibare ibonetse</p>
            <button onClick={load} style={{
              padding: "10px 22px", background: "#0047AB",
              border: "none", borderRadius: 8, color: "#fff",
              cursor: "pointer", fontWeight: 700, fontSize: "0.84rem",
              minHeight: 44, touchAction: "manipulation",
              boxShadow: "0 4px 12px rgba(0,71,171,0.25)",
            }}>
              Ongera Gerageza
            </button>
          </div>
        )}

        {/* ── Content ── */}
        {!loading && stats && (
          <>
            {/* ABATURAGE */}
            <SectionTitle icon="people-fill" title="Abaturage" colorKey="blue" />
            <Grid cols={4}>
              <StatCard icon="people-fill"     label="Abaturage Bose" value={tot}                  colorKey="blue" />
              <StatCard icon="gender-male"     label="Gabo"           value={stats.abaturage_gabo} colorKey="sky"
                sub={`${pct(stats.abaturage_gabo, tot)}%`} progress={pct(stats.abaturage_gabo, tot)} />
              <StatCard icon="gender-female"   label="Gore"           value={stats.abaturage_gore} colorKey="rose"
                sub={`${pct(stats.abaturage_gore, tot)}%`} progress={pct(stats.abaturage_gore, tot)} />
              <StatCard icon="house-door-fill" label="Ingo"           value={stats.ingo_total}     colorKey="teal" />
            </Grid>

            {/* UBUKWE */}
            <SectionTitle icon="heart-fill" title="Imiterere y'Ubukwe" colorKey="rose" />
            <Grid cols={2}>
              <StatCard icon="heart-fill"  label="Bashyingiwe" value={stats.abaturage_yashyingiwe} colorKey="rose"
                sub={`${pct(stats.abaturage_yashyingiwe, tot)}%`} progress={pct(stats.abaturage_yashyingiwe, tot)} />
              <StatCard icon="person-fill" label="Ingaragu"    value={stats.abaturage_ingaragu}    colorKey="violet"
                sub={`${pct(stats.abaturage_ingaragu, tot)}%`} progress={pct(stats.abaturage_ingaragu, tot)} />
            </Grid>

            {/* ABANA */}
            <SectionTitle icon="person-hearts" title="Abana" colorKey="green" />
            <Grid cols={3}>
              <StatCard icon="person-hearts" label="Abana Bose" value={totA}               colorKey="green" />
              <StatCard icon="book-fill"     label="Abiga"      value={stats.abana_ariga}  colorKey="teal"
                sub={`${pct(stats.abana_ariga, totA)}%`} progress={pct(stats.abana_ariga, totA)} />
              <StatCard icon="person-x-fill" label="Batiga"     value={stats.abana_batiga} colorKey="red"
                sub={`${pct(stats.abana_batiga, totA)}%`} progress={pct(stats.abana_batiga, totA)} />
            </Grid>

            {/* URUBYIRUKO */}
            <SectionTitle icon="person-arms-up" title="Urubyiruko" colorKey="amber" />
            <Grid cols={2}>
              <StatCard icon="person-arms-up" label="Urubyiruko Rwose" value={totU}                          colorKey="amber" />
              <StatCard icon="briefcase-fill" label="Rufite Akazi"     value={stats.urubyiruko_rufite_akazi} colorKey="lime"
                sub={`${pct(stats.urubyiruko_rufite_akazi, totU)}%`} progress={pct(stats.urubyiruko_rufite_akazi, totU)} />
            </Grid>

            {/* ABASHESHE & AMAKIMBIRANE */}
            <SectionTitle icon="heart-pulse-fill" title="Abageze mu Izabukuru & Amakimbirane" colorKey="red" />
            <Grid cols={3}>
              <StatCard icon="heart-pulse-fill"          label="Abageze mu Izabukuru" value={stats.abasheshe_total}          colorKey="red" />
              <StatCard icon="exclamation-triangle-fill" label="Amakimbirane Yose"  value={stats.amakimbirane_total}       colorKey="orange" />
              <StatCard icon="fire"                      label="Biracyariho"        value={stats.amakimbirane_biracyariho} colorKey="amber"
                sub={`${pct(stats.amakimbirane_biracyariho, totM)}%`} progress={pct(stats.amakimbirane_biracyariho, totM)} />
            </Grid>

            {/* IMISANZU & INKUNGA */}
            <SectionTitle icon="currency-exchange" title="Imisanzu & Inkunga" colorKey="sky" />
            <Grid cols={3}>
              <StatCard icon="flag-fill"       label="Umusanzu FPR"     value={`${Number(stats.umusanzu_fpr_total     || 0).toLocaleString()} Frw`} colorKey="blue" />
              <StatCard icon="piggy-bank-fill" label="Umusanzu EjoHeza" value={`${Number(stats.umusanzu_ejoheza_total || 0).toLocaleString()} Frw`} colorKey="fuchsia" />
              <StatCard icon="gift-fill"       label="Inkunga za Leta"  value={stats.inkunga_total}                                                  colorKey="violet" />
            </Grid>

            {/* IBIZA */}
            <SectionTitle icon="fire" title="Ibiza" colorKey="red" />
            <Grid cols={2}>
              <StatCard icon="fire" label="Ibiza Byanditswe" value={stats.ibiza_total} colorKey="red" />
              <ShieldCard />
            </Grid>

            {/* footer */}
            <div style={{
              textAlign: "center",
              marginTop: "clamp(20px,3.5vw,36px)",
              color: "#94a3b8",
              fontSize: "clamp(0.6rem,1.2vw,0.68rem)",
            }}>
              <i className="bi bi-shield-lock-fill" style={{ marginRight: 5 }} />
              Imibare yavuguruwe: {new Date().toLocaleString("rw-RW")}
            </div>
          </>
        )}
      </div>
    </>
  );
}
